import { useState } from "react";
import { PartyPopper } from "lucide-react";
import { api } from "../lib/api";
import { Reveal } from "../components/Reveal";
import Seo from "../components/Seo";

const EMPTY = {
  name: "", email: "", phone: "", item_type: "A bag", quantity: 1, colors: "",
  dimensions: "", inspiration: "", budget: "", requested_date: "", gift: false, notes: "",
};

export default function CustomOrdersPage() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ref, setRef] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await api("/custom-orders", { method: "POST", body: { ...form, quantity: Number(form.quantity) || 1 } });
      setRef(res.ref);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (ref) {
    return (
      <div className="bg-butter-soft min-h-screen flex items-center justify-center p-6">
        <div className="bg-white rounded-cloud shadow-card p-10 max-w-md text-center" data-testid="custom-order-success">
          <PartyPopper className="w-14 h-14 text-candy mx-auto mb-4" />
          <h1 className="font-display font-semibold text-3xl">Request received!</h1>
          <p className="text-muted mt-3 font-semibold">Keep this reference number handy:</p>
          <p className="mt-2 font-display font-semibold text-2xl text-candy-deep tracking-wider" data-testid="custom-order-ref">{ref}</p>
          <p className="text-muted text-sm mt-4 font-semibold">Rachel's team will reply to <strong>{form.email}</strong> with color ideas and a quote for the grown-ups to approve.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-butter-soft min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-14">
        <Seo title="Custom Orders" description="Request a made-to-order crochet piece in your own colors — Rachel will dream it up with you." path="/custom-orders" />
        <Reveal className="text-center">
          <h1 data-testid="custom-orders-heading" className="font-display font-semibold text-3xl sm:text-4xl">Your colors. Your cozy.</h1>
          <p className="text-ink/70 font-semibold mt-3">Tell us your idea — a grown-up buyer submits this form, and Rachel will dream it up with you.</p>
        </Reveal>
        <form onSubmit={submit} data-testid="custom-order-form" className="mt-8 bg-white rounded-cloud shadow-card p-6 sm:p-8 grid sm:grid-cols-2 gap-4">
          {[
            { k: "name", label: "Your name", required: true, testid: "custom-name-input" },
            { k: "email", label: "Email", type: "email", required: true, testid: "custom-email-input" },
            { k: "phone", label: "Phone (optional)", testid: "custom-phone-input" },
          ].map((f) => (
            <label key={f.k} className={f.k === "name" || f.k === "email" ? "" : "sm:col-span-1"}>
              <span className="text-sm font-bold text-ink/80">{f.label}</span>
              <input data-testid={f.testid} type={f.type || "text"} required={f.required} value={form[f.k]} onChange={set(f.k)} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold" />
            </label>
          ))}
          <label>
            <span className="text-sm font-bold text-ink/80">What should Rachel make?</span>
            <select data-testid="custom-item-type-select" value={form.item_type} onChange={set("item_type")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold">
              {["A bag", "A plushie", "A wearable", "Something else entirely"].map((o) => <option key={o}>{o}</option>)}
            </select>
          </label>
          <label>
            <span className="text-sm font-bold text-ink/80">Quantity</span>
            <input data-testid="custom-quantity-input" type="number" min={1} max={100} value={form.quantity} onChange={set("quantity")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold" />
          </label>
          <label className="sm:col-span-2">
            <span className="text-sm font-bold text-ink/80">Colors you're dreaming of</span>
            <input data-testid="custom-colors-input" value={form.colors} onChange={set("colors")} placeholder="Lilac + mint + a hint of gold…" className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold" />
          </label>
          <label className="sm:col-span-2">
            <span className="text-sm font-bold text-ink/80">Size or dimensions (optional)</span>
            <input data-testid="custom-dimensions-input" value={form.dimensions} onChange={set("dimensions")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold" />
          </label>
          <label className="sm:col-span-2">
            <span className="text-sm font-bold text-ink/80">Inspiration — describe it! (optional)</span>
            <textarea data-testid="custom-inspiration-input" rows={3} value={form.inspiration} onChange={set("inspiration")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold resize-none" />
          </label>
          <label>
            <span className="text-sm font-bold text-ink/80">Budget (optional)</span>
            <input data-testid="custom-budget-input" value={form.budget} onChange={set("budget")} placeholder="$40–60" className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold" />
          </label>
          <label>
            <span className="text-sm font-bold text-ink/80">Needed by (optional)</span>
            <input data-testid="custom-date-input" type="date" value={form.requested_date} onChange={set("requested_date")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold" />
          </label>
          <label className="sm:col-span-2 flex items-center gap-3 bg-candy-mist rounded-2xl px-4 py-3">
            <input data-testid="custom-gift-checkbox" type="checkbox" checked={form.gift} onChange={set("gift")} className="w-5 h-5 accent-pink-500" />
            <span className="font-bold text-sm">It's a gift — please include a happy wrap 🎁</span>
          </label>
          <label className="sm:col-span-2">
            <span className="text-sm font-bold text-ink/80">Anything else?</span>
            <textarea data-testid="custom-notes-input" rows={2} value={form.notes} onChange={set("notes")} className="mt-1 w-full px-4 py-3 rounded-2xl bg-butter-soft/60 border border-candy-soft focus:border-candy outline-none font-semibold resize-none" />
          </label>
          {error && <p className="sm:col-span-2 text-red-500 text-sm font-bold" data-testid="custom-order-error">{error}</p>}
          <button disabled={busy} data-testid="custom-order-submit" className="sm:col-span-2 py-4 rounded-full bg-candy hover:bg-candy-deep disabled:opacity-60 text-white font-display font-semibold text-lg shadow-pink transition-colors duration-200">
            {busy ? "Sending…" : "Send my custom idea"}
          </button>
        </form>
      </div>
    </div>
  );
}
